import React, { useState, useEffect } from "reactn";
import PropTypes from "prop-types";

import { getShopBasket, getCapBasketSummary } from "../../../../services/Api";
import { getCookie } from "../../../../helpers/Helpers";

import { BasketContainer, BasketTabs, BasketHeader, BasketPanel, BasketTotal, BasketEmpty } from "./BasketSummary.styled";

const BasketTotalRow = ({ totalPrice, basketUrl }) => (
    <BasketTotal>
        <div>
            <span>Total</span>
            <span>
                <span>{totalPrice}</span>
            </span>
        </div>
        <a className="bn bn--primary gtm-cta" href={basketUrl}>
            View basket
        </a>
    </BasketTotal>
);

BasketTotalRow.propTypes = {
    totalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basketUrl: PropTypes.string
};

const EmptyCAPBasket = () => (
    <BasketEmpty>
        <div>
            <p>Your basket is empty.</p>
            <p>Go to:</p>
            <ul>
                <li>
                    <a className="gtm-linkclick" href="/Post-Parcels/Sending/Calculate-Postage">
                        Add a label
                    </a>
                </li>
                <li>
                    <a className="gtm-linkclick" href="/Post-Parcels/Click-and-Post/Returns">
                        Book a return
                    </a>
                </li>
            </ul>
        </div>
        <a className="bn bn--primary gtm-cta" href="/Post-Parcels/Click-and-Post">
            Go to Click &amp; Post
        </a>
    </BasketEmpty>
);

const EmptyShopBasket = () => (
    <BasketEmpty>
        <div>
            <p>Your basket is empty.</p>
            <p>Shop our:</p>
            <ul>
                <li>
                    <a className="gtm-linkclick" href="/Shop/Stamps-Labels">
                        Stamps &amp; labels
                    </a>
                </li>
                <li>
                    <a className="gtm-linkclick" href="/Shop/Special-issue-stamps">
                        Special issue stamps
                    </a>
                </li>
                <li>
                    <a className="gtm-linkclick" href="/Shop/Collectibles-gifts">
                        Collectibles &amp; Gifts
                    </a>
                </li>
                <li>
                    <a className="gtm-linkclick" href="/Shop/Mobile/Phones">
                        Phones
                    </a>
                </li>
            </ul>
        </div>
        <a className="bn bn--primary gtm-cta" href="/Shop/Cart">
            Go to Shop
        </a>
    </BasketEmpty>
);

const BasketSummary = ({ containerNode }) => {
    const [openPanel, setOpenPanel] = useState("cap");
    const [shopBasketData, setShopBasketData] = useState(null);
    const [capBasketData, setCapBasketData] = useState(null);

    const onTabClick = selectedPanel => {
        setOpenPanel(selectedPanel);
    };

    const calculateTotalItemPrice = (price, units) => {
        const totalPriceFixed = (price * units).toFixed(2);
        return `€${totalPriceFixed}`;
    };

    const getCapBasketItemCount = serviceType => {
        return capBasketData.services.find(item => item.service.contains(serviceType))?.count || 0;
    };

    const getCapBasketItemPrice = serviceType => {
        const itemPrice = capBasketData.services.find(item => item.service.contains(serviceType))?.amount || 0;
        return `€${itemPrice.toFixed(2)}`;
    };

    useEffect(() => {
        const shopBasketRef = getCookie("CMSShoppingCart");
        const userIdCookie = getCookie("CIAMCustomerID");
        const capBasketRefCookie = getCookie("ClickPostBasketReference");

        if (shopBasketRef) {
            getShopBasket()
                .then(res => {
                    if (res.status === 200 && res.data?.cart) {
                        if (res.data?.cart?.cartItems.length > 0) {
                            setShopBasketData(res.data.cart);
                        }
                    }
                })
                .catch(err => {
                    console.log(err);
                });
        }

        if (capBasketRefCookie) {
            getCapBasketSummary(userIdCookie)
                .then(res => {
                    if (res.status === 200 && res.data) {
                        setCapBasketData(res.data);
                    }
                })
                .catch(() => {
                    setOpenPanel("shop");
                });
        }
    }, []);

    useEffect(() => {
        if (shopBasketData || capBasketData) {
            containerNode.parentElement.classList.add("m124__split", "m124__split--basket");
        } else {
            containerNode.parentElement.classList.remove("m124__split", "m124__split--basket");
        }
    }, [shopBasketData, capBasketData]);

    return (
        <>
            {(shopBasketData || capBasketData) && (
                <BasketContainer>
                    <h5>My baskets</h5>
                    <BasketTabs>
                        <BasketHeader role="tablist" aria-label="Basket summary">
                            <button className="gtm-tab" type="button" role="tab" aria-controls="capTab" id="capTabAriaId" aria-selected={openPanel === "cap"} onClick={() => onTabClick("cap")}>
                                Click & Post
                                <span>{capBasketData?.itemCount || 0}</span>
                            </button>
                            <button className="gtm-tab" type="button" role="tab" aria-controls="shopTab" id="shopTabAriaId" aria-selected={openPanel === "shop"} onClick={() => onTabClick("shop")}>
                                Shop
                                <span>{shopBasketData?.cartItems?.reduce((acc, item) => acc + item.quantity, 0) || 0}</span>
                            </button>
                        </BasketHeader>
                        {openPanel === "cap" ? (
                            <BasketPanel id="capTab" role="tabpanel" aria-labelledby="capTabAriaId">
                                {capBasketData ? (
                                    <>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th colSpan="3">Item</th>
                                                    <th>Qty</th>
                                                    <th>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td colSpan="3">Postage labels</td>
                                                    <td>
                                                        <span>{getCapBasketItemCount("Mails")}</span>
                                                    </td>
                                                    <td>
                                                        <span>{getCapBasketItemPrice("Mails")}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="3">Shopping returns</td>
                                                    <td>
                                                        <span>{getCapBasketItemCount("Returns")}</span>
                                                    </td>
                                                    <td>
                                                        <span>{getCapBasketItemPrice("Returns")}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="3">Sub-total</td>
                                                    <td></td>
                                                    <td>
                                                        <span>&euro;</span>
                                                        <span>{capBasketData.totalAmount.toFixed(2)}</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <BasketTotalRow totalPrice={`€${capBasketData.totalAmount.toFixed(2)}`} basketUrl="/Post-Parcels/Self-Service/Basket" />
                                    </>
                                ) : (
                                    <EmptyCAPBasket />
                                )}
                            </BasketPanel>
                        ) : (
                            <BasketPanel id="shopTab" role="tabpanel" aria-labelledby="shopTabAriaId" className="basket-summary-shop-panel">
                                {shopBasketData ? (
                                    <>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th colSpan="3">Item</th>
                                                    <th>Qty</th>
                                                    <th>Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shopBasketData.cartItems.map((item, i) => (
                                                    <tr key={i}>
                                                        <td colSpan="2">{item.name}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{calculateTotalItemPrice(item.unitPrice, item.quantity)}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <td colSpan="3">Sub-total</td>
                                                    <td>
                                                        <span>{shopBasketData.subtotalPrice}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td colSpan="3">Shipping</td>
                                                    <td>
                                                        <span>{shopBasketData.shippingPrice}</span>
                                                    </td>
                                                </tr>
                                                {parseFloat(shopBasketData.discountPrice.replace("€", "")) > 0 && (
                                                    <tr>
                                                        <td colSpan="3">Discount</td>
                                                        <td>
                                                            <span>{shopBasketData.discountPrice}</span>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        <BasketTotalRow totalPrice={shopBasketData.totalPrice} basketUrl="/Shop/Cart" />
                                    </>
                                ) : (
                                    <EmptyShopBasket />
                                )}
                            </BasketPanel>
                        )}
                    </BasketTabs>
                </BasketContainer>
            )}
        </>
    );
};

export default BasketSummary;

BasketSummary.propTypes = {
    containerNode: PropTypes.instanceOf(Element)
};
